import { systemFromCodeableConcept } from '@seen/core/codeableConcept'
import * as codeSystems from '@seen/core/codeSystems'
import { invariant } from '@seen/core/invariant'
import { AssessmentTemplate } from '@seen/core/models/assessmentTemplate'
import type { LibrarySection } from '@seen/core/models/compositionLibraryEncoded'
import { IncidentTemplate } from '@seen/core/models/incidentTemplate'
import { NoteTemplate } from '@seen/core/models/noteTemplate'
import { OrderTemplate } from '@seen/core/models/orderTemplate'
import { Button, cn, DotSeparator, Item, ItemContent } from '@seen/ui'
import { IconCheck } from '@tabler/icons-react'
import { type ReactNode, useCallback, useMemo, useState } from 'react'
import { Card } from '#src/components/core/Card.tsx'
import {
  Command,
  CommandItem,
  CommandList,
} from '#src/components/core/Command.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { RichText } from '#src/components/RichText.tsx'
import { ValidatedToolSectionPreview } from '#src/components/ValidatedToolSectionPreview.tsx'
import { useDidChange } from '#src/hooks/useDidChange.ts'
import { useResourceVersions } from '#src/hooks/useResourceVersions.ts'
import { parseNoteContent } from '#src/utils/noteContent.ts'

type TemplateType =
  | NoteTemplate
  | AssessmentTemplate
  | OrderTemplate
  | IncidentTemplate

export interface TemplateHistoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  templateId: string | undefined
  onRestoreVersion: (template: TemplateType) => void
}

export function TemplateHistoryDialog({
  open,
  onOpenChange,
  templateId,
  onRestoreVersion,
}: TemplateHistoryDialogProps): ReactNode {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogDisplay notScrollable size="lg">
        <TemplateHistoryDialogContent
          templateId={templateId}
          onRestoreVersion={(template) => {
            onRestoreVersion(template)
            onOpenChange(false)
          }}
        />
      </DialogDisplay>
    </Dialog>
  )
}

function TemplateHistoryDialogContent({
  templateId,
  onRestoreVersion,
}: {
  templateId: string | undefined
  onRestoreVersion: (template: TemplateType) => void
}): ReactNode {
  const { data: versions = [], isLoading } = useResourceVersions(
    [NoteTemplate, AssessmentTemplate, OrderTemplate, IncidentTemplate],
    templateId
  )

  const latestVersion = versions?.[0]?.versionId
  const [selectedVersion, setSelectedVersion] = useState<string | null>(
    latestVersion ?? null
  )
  const latestVersionExists = !!latestVersion
  const didLatestVersionExistsChange = useDidChange(latestVersionExists)
  if (didLatestVersionExistsChange) {
    setSelectedVersion(latestVersion ?? null)
  }

  const selectedTemplate = useMemo(() => {
    return versions?.find((v) => v.versionId === selectedVersion)
  }, [versions, selectedVersion])

  const handleRestoreVersion = useCallback(() => {
    if (selectedTemplate) {
      onRestoreVersion(selectedTemplate)
    }
  }, [selectedTemplate, onRestoreVersion])

  if (isLoading) {
    return (
      <div className="flex flex-col">
        <DialogTitle className="p-6">Version History</DialogTitle>
        <DialogContent className="min-h-0 flex-1">
          <div className="flex min-h-0 flex-1 gap-4">
            <Card className="flex max-w-[calc(65ch+2rem)] min-w-[calc(65ch+2rem)] flex-col p-0 text-sm">
              <div className="h-screen">
                <Loader />
              </div>
            </Card>
            <Card className="flex-1 p-0">
              <Loader />
            </Card>
          </div>
        </DialogContent>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <DialogTitle className="flex items-center gap-4 p-6">
        <span>Version History</span>
        {selectedVersion !== latestVersion && (
          <div className="-my-4">
            <Button onClick={handleRestoreVersion}>Restore this version</Button>
          </div>
        )}
      </DialogTitle>
      <DialogContent className="min-h-0 flex-1">
        <div className="flex min-h-0 flex-1 gap-4">
          <Card className="flex max-w-[calc(65ch+2rem)] min-w-[calc(65ch+2rem)] flex-col p-0 text-sm">
            <ScrollArea
              scrollRestorationId={`template-${templateId}-version-${selectedVersion}`}
            >
              <TemplatePreview template={selectedTemplate} />
            </ScrollArea>
          </Card>
          <Card className="flex-1 p-0">
            <Command>
              <CommandList className="max-h-none">
                {versions?.map((v) => {
                  invariant(v.lastUpdated, 'Version must have a lastUpdated')
                  const author = v.lastUpdatedByPractitioner
                  invariant(author, 'Version must have a Practitioner author')
                  return (
                    <CommandItem
                      key={v.versionId}
                      value={v.versionId}
                      onSelect={() => setSelectedVersion(v.versionId)}
                    >
                      <div className="flex flex-1 items-center justify-between">
                        <div className="ml-0.5 flex items-center gap-1">
                          <PractitionerNameplate practitioner={author} />
                          <DotSeparator />
                          <HoverDate date={v.lastUpdated} format="datetime" />
                        </div>

                        <IconCheck
                          className={cn(
                            'mr-0.5 size-4 shrink-0 opacity-0',
                            selectedVersion === v.versionId && 'opacity-100'
                          )}
                        />
                      </div>
                    </CommandItem>
                  )
                })}
              </CommandList>
            </Command>
          </Card>
        </div>
      </DialogContent>
    </div>
  )
}

function TemplatePreview({
  template,
}: {
  template: TemplateType | undefined
}): ReactNode {
  if (!template) {
    return null
  }

  if (template instanceof NoteTemplate) {
    return <RichText className="p-4" value={template.data} />
  }

  // Multi-section templates (Assessment, Order, Incident)
  const sections = (
    template as AssessmentTemplate | OrderTemplate | IncidentTemplate
  ).sections

  return (
    <div className="flex flex-col gap-4 p-4">
      {sections.map((section: LibrarySection, idx: number) =>
        isValidatedToolSection(section) ? (
          <ValidatedToolSectionPreview
            key={section.id ?? idx}
            section={section}
          />
        ) : (
          <Item key={section.id ?? idx} variant="outline">
            <ItemContent>
              <RichText value={parseNoteContent(section.text?.div ?? '')} />
            </ItemContent>
          </Item>
        )
      )}
    </div>
  )
}

function isValidatedToolSection(section: LibrarySection): boolean {
  return (
    systemFromCodeableConcept(section.code) ===
    codeSystems.ASSESSMENT_SECTION_VALIDATED_TOOL
  )
}
